import { Component, Input, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DialogConfirmacaoComponent } from '@components/dialog-confirmacao/dialog-confirmacao.component';
import { Popover } from 'primeng/popover';
import { firstValueFrom } from 'rxjs';

import { MessageService } from '@services/message.service';
import { ItemVistoriaComodoModelo } from '../../../editar-modelo-vistoria/models/item-vistoria-comodo-modelo';
import { ItemVistoriaModelo } from '../../../editar-modelo-vistoria/models/item-vistoria-modelo';
import {
  AtributoPreenchimento, EstadoConservacao, FotoVistoria, ItemVistoriaPreenchimento, opcoesEstadoConservacao, opcoesTipoAtributo
} from '../../models/item-vistoria-preenchimento';
import { FotosVistoriaService, RecorteFoto } from '../../services/fotos-vistoria.service';
import { RascunhoVistoriaService } from '../../services/rascunho-vistoria.service';
import { DadosDialogRecorteFoto, DialogRecorteFotoComponent } from '../dialog-recorte-foto/dialog-recorte-foto.component';

@Component({
  selector: 'app-itens-comodo-vistoria',
  standalone: false,
  templateUrl: './itens-comodo-vistoria.component.html',
  styleUrl: './itens-comodo-vistoria.component.scss'
})
export class ItensComodoVistoriaComponent {

  @Input() itensVistoria: ItemVistoriaPreenchimento[] = [];
  @Input() itensModelo: ItemVistoriaComodoModelo[] = [];
  @Input() poolItensModelo: ItemVistoriaModelo[] = [];
  @Input() vistoriaId = 0;
  @Input() editarModelo = false;

  @ViewChild('popoverEstado') popoverEstado!: Popover;
  @ViewChild('popoverFotos') popoverFotos!: Popover;

  itemPopoverEstado: ItemVistoriaPreenchimento | null = null;
  atributoPopoverFotos: AtributoPreenchimento | null = null;
  fotoAmpliada: FotoVistoria | null = null;
  readonly opcoesEstadoConservacao = opcoesEstadoConservacao;
  readonly opcoesTipoAtributo = opcoesTipoAtributo;

  constructor(
    private dialog: MatDialog,
    private messageService: MessageService,
    public fotosVistoriaService: FotosVistoriaService,
    private rascunhoVistoriaService: RascunhoVistoriaService,
  ) { }

  /** Mantém os painéis do accordion quando a tela troca o array pelo devolvido no salvar. */
  public trackPorIndice(indice: number): number {
    return indice;
  }

  /**
   * Atributo criado pelo "Adicionar atributo" e ainda não salvo. Usa o id, não o nomeModelo: o nomeModelo
   * só existe na tela e volta nulo do salvar, o que faria todo atributo salvo parecer avulso.
   */
  public ehAtributoAvulsoNovo(atributo: AtributoPreenchimento): boolean {
    return atributo.id === 0 && atributo.nomeModelo === null;
  }

  public nomeAtributoEditavel(atributo: AtributoPreenchimento): boolean {
    return this.ehAtributoAvulsoNovo(atributo) || this.editarModelo;
  }

  get itensDisponiveis(): ItemVistoriaModelo[] {
    return [...this.itensModelo]
      .sort((a, b) => a.ordem - b.ordem)
      .map(vinculo => vinculo.item);
  }

  get itensForaDoComodo(): ItemVistoriaModelo[] {
    const idsDoComodo = this.itensModelo.map(vinculo => vinculo.item.id);
    return this.poolItensModelo.filter(item => !idsDoComodo.includes(item.id));
  }

  public adicionarItem(item: ItemVistoriaModelo | null): void {
    if (!item) return;

    this.itensVistoria.push({
      id: 0,
      nomeModelo: item.nome,
      nome: item.nome,
      atributos: [...item.atributos]
        .sort((a, b) => a.ordem - b.ordem)
        .map(a => ({ id: 0, nomeModelo: a.nome, nome: a.nome, ehFoto: a.ehFoto, valor: '', fotos: [] })),
      estadoConservacao: null,
      observacao: '',
    });
  }

  public adicionarItemAvulso(): void {
    this.itensVistoria.push({
      id: 0,
      nomeModelo: null,
      nome: '',
      atributos: [],
      estadoConservacao: null,
      observacao: '',
    });
  }

  public adicionarAtributoAvulso(itemVistoria: ItemVistoriaPreenchimento): void {
    itemVistoria.atributos.push({ id: 0, nomeModelo: null, nome: '', ehFoto: false, valor: '', fotos: [] });
  }

  public confirmarRemocaoAtributo(itemVistoria: ItemVistoriaPreenchimento, atributo: AtributoPreenchimento): void {
    const dialogRef = this.dialog.open(DialogConfirmacaoComponent, {
      width: '467px',
      panelClass: 'no-padding-dialog',
      disableClose: true,
    });
    dialogRef.componentInstance.titulo = 'Remover atributo';
    dialogRef.componentInstance.mensagem = `O atributo "${atributo.nome || '-'}" será removido.`;
    dialogRef.componentInstance.textoBtnConfirmacao = 'Remover';
    dialogRef.componentInstance.btnBackgroundColor = '#F44336';
    dialogRef.componentInstance.acaoConfirmacao = () => this.removerAtributo(itemVistoria, atributo);
  }

  public labelEstado(estado: EstadoConservacao | null): string {
    return opcoesEstadoConservacao.find(o => o.value === estado)?.label ?? 'Não informado';
  }

  public abrirPopoverEstado(event: Event, itemVistoria: ItemVistoriaPreenchimento): void {
    this.itemPopoverEstado = itemVistoria;
    this.popoverEstado.toggle(event);
  }

  public abrirPopoverFotos(event: Event, atributo: AtributoPreenchimento): void {
    this.atributoPopoverFotos = atributo;
    this.popoverFotos.toggle(event);
  }

  public ampliarFoto(foto: FotoVistoria): void {
    this.popoverFotos.hide();
    this.fotoAmpliada = foto;
  }

  public fecharFotoAmpliada(): void {
    this.fotosVistoriaService.liberarFotoCheia(this.fotoAmpliada!);
    this.fotoAmpliada = null;
  }

  /** Grava o rascunho antes de abrir a câmera: com ela aberta, o Android pode encerrar o Chrome a qualquer
   * momento. O click do input fica síncrono (precisa estar no gesto do usuário); a gravação leva milissegundos
   * e termina antes de o Chrome ir pra segundo plano. */
  public tirarFoto(cameraInput: HTMLInputElement): void {
    this.rascunhoVistoriaService.solicitarGravacao();
    cameraInput.click();
  }

  /** Cada arquivo passa pelo recorte quadrado (obrigatório; cancelado = descartado) e vira uma foto pendente
   * (reduzida no aparelho), na ordem em que foi tirada. Sobe no próximo Salvar. */
  public async onFotosSelecionadas(event: Event, atributo: AtributoPreenchimento): Promise<void> {
    const input = event.target as HTMLInputElement;
    const arquivos = Array.from(input.files ?? []).sort((a, b) => a.lastModified - b.lastModified);
    input.value = '';

    // Conta a seleção inteira (não foto a foto), pra o Salvar não reabilitar entre uma foto e outra
    // nem enquanto o recorte está aberto.
    this.fotosVistoriaService.processando++;
    try {
      for (const [indice, arquivo] of arquivos.entries()) {
        const recorte = await this.recortarFoto(arquivo, indice + 1, arquivos.length);
        if (!recorte) continue;

        try {
          atributo.fotos.push(await this.fotosVistoriaService.criarFotoPendente(arquivo, recorte));
          this.rascunhoVistoriaService.solicitarGravacao();
        } catch {
          this.messageService.showToastWarning(`Não foi possível ler a foto "${arquivo.name}"`);
        }
      }
    } finally {
      this.fotosVistoriaService.processando--;
    }
  }

  private recortarFoto(arquivo: File, posicao: number, total: number): Promise<RecorteFoto | null | undefined> {
    const dialogRef = this.dialog.open<DialogRecorteFotoComponent, DadosDialogRecorteFoto, RecorteFoto | null>(
      DialogRecorteFotoComponent, {
        data: { arquivo, posicao, total },
        width: '100vw',
        height: '100dvh',
        maxWidth: '100vw',
        maxHeight: '100dvh',
        panelClass: 'dialog-tela-cheia',
        disableClose: true,
      });
    return firstValueFrom(dialogRef.afterClosed());
  }

  public confirmarRemocaoFoto(atributo: AtributoPreenchimento, foto: FotoVistoria): void {
    const dialogRef = this.dialog.open(DialogConfirmacaoComponent, {
      width: '467px',
      panelClass: 'no-padding-dialog',
      disableClose: true,
    });
    dialogRef.componentInstance.titulo = 'Remover foto';
    dialogRef.componentInstance.mensagem = 'A foto será removida ao salvar a vistoria.';
    dialogRef.componentInstance.textoBtnConfirmacao = 'Remover';
    dialogRef.componentInstance.btnBackgroundColor = '#F44336';
    dialogRef.componentInstance.acaoConfirmacao = () => atributo.fotos.splice(atributo.fotos.indexOf(foto), 1);
  }

  public confirmarRemocao(itemVistoria: ItemVistoriaPreenchimento): void {
    const dialogRef = this.dialog.open(DialogConfirmacaoComponent, {
      width: '467px',
      panelClass: 'no-padding-dialog',
      disableClose: true,
    });
    dialogRef.componentInstance.titulo = 'Remover item';
    dialogRef.componentInstance.mensagem = `O item "${itemVistoria.nome || '-'}" será removido.`;
    dialogRef.componentInstance.textoBtnConfirmacao = 'Remover';
    dialogRef.componentInstance.btnBackgroundColor = '#F44336';
    dialogRef.componentInstance.acaoConfirmacao = () => this.removerItem(itemVistoria);
  }

  private removerItem(itemVistoria: ItemVistoriaPreenchimento): void {
    this.itensVistoria.splice(this.itensVistoria.indexOf(itemVistoria), 1);
  }

  private removerAtributo(itemVistoria: ItemVistoriaPreenchimento, atributo: AtributoPreenchimento): void {
    itemVistoria.atributos.splice(itemVistoria.atributos.indexOf(atributo), 1);
  }
}
